import React from "react";
import ThemeProvider from "@onnit-js/ui/components/themes/ThemeProvider";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import theme from "@onnit-js/ui/components/themes/light";
import Box from "@onnit-js/ui/components/box/Box";
import { List, ListItem, Text } from "@onnit-js/ui/components/text";
import { Img, Picture } from "@onnit-js/ui/components/image";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import Sup from "@onnit-js/ui/components/text/Sup";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import ImageContentPortrait from "@onnit-js/ui/components/module/ImageContentPortrait";
import useSelectedProduct from "@onnit-js/ui/hooks/useSelectedProduct";
import OnnitContext from "@onnit-js/ui/@types/interfaces/OnnitContext";
import config from "../../config/config";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";
import usePageData from "../../hooks/usePageData";
import UsageAndDisclaimers from "../../components/common/UsageAndDisclaimers";
import SupplementFacts from "../../components/common/SupplementFacts";
import OnnitPageData from "../../interfaces/OnnitPageData";

interface Props {
    pageData: OnnitPageData;
    onnitContext: OnnitContext;
}

const imageUrls: Record<number, string> = {
    5587: "2023-CREATINE-Wpage-Gallery-1-920x920.png",
    9539: "9539-2x-creatine.png",
    9540: "9540-3x-creatine.png"
};

const Content:React.FC<Props> = ({ pageData, onnitContext }) => {
    const { selectedProduct, setProduct } = useSelectedProduct(pageData.products);

    const IMG_URL = `${onnitContext.url.cdn.static_images}/images/product-page/creatine`;
    const { IMG_PARAMS } = config;
    const colors = {
        lightBlue: "#48A4D5",
        blackish: "#161616",
    };

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />
                <DefaultProductPageTop onProductChange={(product) => {
                    if (product.id === selectedProduct.id) {
                        return;
                    }
                    const url = new URL(window.location.href);
                    const params = url.searchParams;
                    params.set("pid", product.id.toString());
                    window.history.replaceState({}, "", `${url.pathname}?${params.toString()}`);
                    setProduct(product);
                }}
                >
                    <ProductImageGrid variant="layout3">
                        <Img
                            src={`${IMG_URL}/${imageUrls[selectedProduct.id]}${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Tub of unflavored creatine."
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/2023-CREATINE-Wpage-Gallery-2-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Tub of unflavored creatine."
                            width={920}
                            height={920}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={424}
                            src={[
                                `${IMG_URL}/2023-CREATINE-Wpage-Gallery-3-920x920.png${IMG_PARAMS}`,
                                `${IMG_URL}/2023-CREATINE-Wpage-Gallery-3-1940x873.png${IMG_PARAMS}`,
                            ]}
                            alt="Man performing flies with text, 'Promotes stength and power.'"
                        />
                        <Img
                            src={`${IMG_URL}/2023-CREATINE-Wpage-Gallery-4-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Man resting after rower with text, 'Aids changes in body composition.'"
                            width={920}
                            height={920}
                        />
                        <Img
                            src={`${IMG_URL}/2023-CREATINE-Wpage-Gallery-5-920x920.png${IMG_PARAMS}`}
                            lazyLoad={false}
                            alt="Water bottle next to creatine tub. Text reads, 'Assists with energy for anaerobic exercise.'"
                            width={920}
                            height={920}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box bg={colors.blackish} py={[6, 6, 6, 8]}>
                        <TwelveColumn display={["none", "none", "grid"]} mb={4}>
                            <Box gridColumn="2 / span 7" px={4}>
                                <Text as="h2" color="grays.3" typeStyle="text02" textTransform="uppercase">
                                    Creatine
                                </Text>
                            </Box>
                        </TwelveColumn>
                        <TwelveColumn display={["block", "block", "grid"]} mb={[0, 0, 6]}>
                            <Box gridColumn="7 / span 5" px={4}>
                                <Img
                                    src={`${IMG_URL}/2023-CREATINE-Wpage-Product-Hero-1920x1080.png`}
                                    alt="Tub of creatine with effects."
                                    aria-hidden="true"
                                    width={558}
                                    height={355}
                                />
                            </Box>
                            <Box
                                gridColumn="2 / span 5"
                                display={["block", "block", "flex"]}
                                alignItems="center"
                                px={4}
                            >
                                <Text
                                    display={["block", "block", "none"]}
                                    as="h2"
                                    color="grays.3"
                                    typeStyle="text02"
                                    textTransform="uppercase"
                                    my={3}
                                >
                                    Creatine
                                </Text>

                                <Text
                                    as="p"
                                    typeStyle="title01"
                                    color={colors.lightBlue}
                                    textTransform="uppercase"
                                    mb={2}
                                >
                                    The evidence is overwhelming
                                </Text>
                            </Box>
                        </TwelveColumn>

                        <TwelveColumn display={["block", "block", "grid"]}>
                            <Box gridColumn="2 / span 7" px={4} mb={[6, 6, 6, 8]}>
                                <Text as="p" color="white" typeStyle="text02" mb={4}>
                                    Creatine monohydrate is one of the most popular and best studied sports nutrition
                                    supplements ever. For more than 30 years, athletes and gym goers have relied
                                    on creatine. When used daily, it has been shown to support short-burst, high-intensity
                                    activity, as well as gains in muscle, strength, and power.<Sup
                                        variant="dagger"
                                    />
                                </Text>
                            </Box>
                            <Box gridColumn="3 / span 7" px={4}>
                                <Text
                                    as="h2"
                                    typeStyle="title01"
                                    mb={4}
                                    textTransform="uppercase"
                                    color={colors.lightBlue}
                                >
                                    Creatine is for:
                                </Text>
                                <List>
                                    <ListItem typeStyle="text02" color="white">
                                        Power and strength athletes
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="white">
                                        Recreational lifters
                                    </ListItem>
                                    <ListItem typeStyle="text02" color="white">
                                        People who want to transform their bodies
                                    </ListItem>
                                </List>
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <ImageContentPortrait
                        reverse
                        image={(
                            <Img
                                alt="Man putting plates on barbell."
                                placeholder={[464, 600]}
                                src={`${IMG_URL}/2023-CREATINE-Wpage-How.png${IMG_PARAMS}`}
                                width={926}
                                height={1200}
                            />
                        )}
                        content={(
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text as="h2" typeStyle="copyDisplay02" mb={[4, 4, 6]}>
                                    How Creatine Works
                                </Text>
                                <Text as="p" typeStyle="text02" mb={[4, 4, 5]}>
                                    Creatine works by aiding in the regeneration of ATP, the fuel source for muscle
                                    contractions. As a result, it can help you get more reps on weight
                                    training exercises, and sustain short-burst anaerobic activities—such as sprinting,
                                    jumping, and martial arts training—with greater power for longer
                                    durations.<Sup variant="dagger" />
                                </Text>
                            </Box>
                        )}
                    />

                </div>

                {pageData.suggested_use && pageData.supplement_facts && (
                    <UsageAndDisclaimers supplementFacts={<SupplementFacts />}>
                        <Text typeStyle="caption02" color="grays.5" mb={4}>
                            <Text as="span" fontWeight="bold" display="inline">WARNING:</Text> Consult a medical doctor
                            before taking this or any other nutritional supplement if you are pregnant,
                            nursing, have or
                            suspect a medical condition, or are
                            taking any medications. <Text as="span" fontWeight="bold" display="inline">Store in a cool, dry place.</Text>{" "}Do not use if freshness seal is missing or broken.
                            <Text as="span" fontWeight="bold" mt={5}>
                                KEEP OUT OF REACH OF CHILDREN.
                            </Text>
                        </Text>
                    </UsageAndDisclaimers>
                )}
            </>
        </ThemeProvider>
    );
};

export default function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();

    if (!pageData || !onnitContext) {
        return null;
    }
    return <Content pageData={pageData} onnitContext={onnitContext} />;
}
